import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageOff } from 'lucide-react';
import type { ImgHTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ThumbnailRetention } from '@/api/thumbnails';
import { thumbnailQuery } from '@/api/thumbnails';
import { cn } from '@/design/utils';
import { useApiClient } from '@/features/auth/session';

/**
 * One person's face, drawn from the batched thumbnail read (`api/thumbnails.ts`).
 *
 * **Every screen that draws a face draws it through here or through {@link usePersonThumbnail}**,
 * and none of them names an image URL any more: an `<img>` per face was a request per face, which
 * is what had a full residence's dashboard spend the whole building's address budget at once.
 *
 * **Four states, and the two failures are visible.** While the face is on its way the call site's
 * own `<img>` is drawn with a transparent placeholder, so a layout, an alt text and an image count
 * do not move when it lands. A face that is `missing` — removed, or its record erased — or `failed`
 * after its retries is drawn as an explicit "could not be loaded" marker, and never as the empty
 * "no photograph" square a list draws for somebody who has none: those are different facts, and at
 * a gate the second is a fault an operator has to see.
 *
 * Loaded eagerly, never on scroll. A thumbnail read counts as activity against the gate's idle
 * window, so a request triggered by scrolling after a few quiet minutes would lock an
 * administrator who is reading the screen.
 */

/** A one-pixel transparent GIF: what the `<img>` holds until the face arrives. */
const PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export type ThumbnailState = 'loading' | 'ready' | 'missing' | 'failed';

/** One face's state, and its bytes once they are here. */
export function usePersonThumbnail(
  imageId: string,
  retention: ThumbnailRetention = 'desk',
): { readonly state: ThumbnailState; readonly src: string | null } {
  const queries = useQueryClient();
  const client = useApiClient();
  const result = useQuery(thumbnailQuery(queries, client, imageId, retention));

  if (result.data?.state === 'ready') {
    return { state: 'ready', src: result.data.src };
  }
  if (result.data?.state === 'missing') {
    return { state: 'missing', src: null };
  }
  // An error is final only once the query has stopped retrying it; until then it is still loading.
  return { state: result.isError ? 'failed' : 'loading', src: null };
}

export function PersonThumbnail({
  imageId,
  retention = 'desk',
  alt,
  className,
  ...image
}: Readonly<
  {
    imageId: string;
    retention?: ThumbnailRetention;
    alt: string;
    className?: string;
  } & Omit<ImgHTMLAttributes<HTMLImageElement>, 'alt' | 'className' | 'src'>
>): ReactNode {
  const { t } = useTranslation();
  const { state, src } = usePersonThumbnail(imageId, retention);

  if (state === 'missing' || state === 'failed') {
    return (
      <span
        role="img"
        aria-label={t('person.images.unavailable')}
        title={t('person.images.unavailable')}
        data-thumbnail-state={state}
        className={cn('grid shrink-0 place-items-center bg-muted text-muted-foreground', className)}
      >
        <ImageOff aria-hidden className="size-1/2" />
      </span>
    );
  }

  return (
    <img
      {...image}
      src={src ?? PLACEHOLDER}
      alt={alt}
      className={className}
      decoding="async"
      data-thumbnail-state={state}
    />
  );
}
